import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { submitAnswer, leaveGame, getState } from '../services/api';
import Avatar from '../components/Avatar';


interface SlotItem {
  type: 'slot' | 'fixed';
  label?: string;
  displayLabel?: string;
  char?: string;
  value: { cardId: string; char: string } | null;
}

interface ARMessage {
  type: string;
  data?: any;
}

const GamePage: React.FC = () => {
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerId = localStorage.getItem('playerId') || '';

  const { isConnected, gameState, error: wsError, setPlayerId } = useSocket();

  // Game UI state
  const [isValidating, setIsValidating] = useState(true);
  const [sentenceStructure, setSentenceStructure] = useState<SlotItem[]>([]);
  const [currentSlotIndex, setCurrentSlotIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<{ correct: boolean; message: string } | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const myPlayer = gameState?.players?.find(p => p.player_id === playerId);
  const showProgressiveResults = gameState?.status === 'playing' && myPlayer?.status === 'finished';
  const currentLevel = gameState?.current_level;

  // Validate game access
  useEffect(() => {
    const validateGameAccess = async () => {
      if (!playerId) {
        navigate('/login', { replace: true });
        return;
      }
      try {
        const state = await getState(playerId);
        const player = state.players?.find(p => p.player_id === playerId);
        if (state.status !== 'playing' || !player) {
          navigate('/lobby', { replace: true });
          return;
        }
        if (!isConnected) {
          setPlayerId(playerId);
        }
        setIsValidating(false);
      } catch (e) {
        navigate('/lobby', { replace: true });
      }
    };
    validateGameAccess();
  }, [playerId, isConnected, setPlayerId, navigate]);

  // Parse grammar pattern into structure
  const parseGrammarPattern = useCallback((pattern: string | undefined): SlotItem[] => {
    if (!pattern) {
      return [
        { type: 'slot', label: 'S', displayLabel: 'S', value: null },
        { type: 'slot', label: 'V', displayLabel: 'V', value: null },
        { type: 'slot', label: 'O', displayLabel: 'O', value: null },
      ];
    }

    const parts = pattern.split('+').map(p => p.trim());
    const structure: SlotItem[] = [];
    const slotCounts: Record<string, number> = { S: 0, V: 0, O: 0, Q: 0 };

    parts.forEach(part => {
      if (['S', 'V', 'O', 'Q'].includes(part)) {
        slotCounts[part]++;
        const displayLabel = slotCounts[part] > 1 ? `${part}${slotCounts[part]}` : part;
        structure.push({ type: 'slot', label: part, displayLabel, value: null });
      } else {
        structure.push({ type: 'fixed', char: part, value: null });
      }
    });

    return structure;
  }, []);

  // Track previous level to avoid unnecessary resets
  const prevLevelIdRef = useRef<number | null>(null);

  // Update sentence structure only when level_id actually changes
  useEffect(() => {
    if (currentLevel && currentLevel.level_id !== prevLevelIdRef.current) {
      prevLevelIdRef.current = currentLevel.level_id;
      setSentenceStructure(parseGrammarPattern(currentLevel.grammar_pattern));
      setCurrentSlotIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLevel?.level_id, currentLevel?.grammar_pattern, parseGrammarPattern]);

  // Get slots only
  const getSlots = useCallback(() => {
    return sentenceStructure.filter(s => s.type === 'slot');
  }, [sentenceStructure]);

  // Add card to current slot
  const addCardToSlot = useCallback((cardId: string, char: string) => {
    if (isSubmitting) return;

    setSentenceStructure(prev => {
      const newStructure = [...prev];
      const slots = newStructure.filter(s => s.type === 'slot');

      if (currentSlotIndex >= slots.length) return prev;

      // Update the current slot with the card value
      let slotIdx = 0;
      for (let i = 0; i < newStructure.length; i++) {
        if (newStructure[i].type === 'slot') {
          if (slotIdx === currentSlotIndex) {
            newStructure[i] = { ...newStructure[i], value: { cardId, char } };
            break;
          }
          slotIdx++;
        }
      }

      // Find next empty slot after update
      const updatedSlots = newStructure.filter(s => s.type === 'slot');
      let nextEmpty = -1;

      // First, look for empty slots after current index
      for (let i = currentSlotIndex + 1; i < updatedSlots.length; i++) {
        if (updatedSlots[i].value === null) {
          nextEmpty = i;
          break;
        }
      }

      // If not found, look from the beginning
      if (nextEmpty === -1) {
        for (let i = 0; i < updatedSlots.length; i++) {
          if (updatedSlots[i].value === null) {
            nextEmpty = i;
            break;
          }
        }
      }

      // Update slot index (need to do this via setTimeout to avoid state issues)
      if (nextEmpty !== -1) {
        setTimeout(() => setCurrentSlotIndex(nextEmpty), 0);
      }

      return newStructure;
    });
  }, [currentSlotIndex, isSubmitting]);

  // Handle messages from AR iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const message = event.data as ARMessage;

      switch (message.type) {
        case 'CARD_SELECTED':
          if (message.data?.cardId && message.data?.char) {
            addCardToSlot(message.data.cardId, message.data.char);
          }
          break;
        case 'AR_READY':
          console.log('AR camera ready');
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [addCardToSlot]);

  // Clear all slots
  const handleClear = () => {
    if (isSubmitting) return;
    setSentenceStructure(prev =>
      prev.map(item => item.type === 'slot' ? { ...item, value: null } : item)
    );
    setCurrentSlotIndex(0);
  };

  // Submit answer
  const handleSubmit = async () => {
    const slots = getSlots();
    const allFilled = slots.every(s => s.value !== null);
    if (!allFilled || isSubmitting || !playerId) return;

    setIsSubmitting(true);
    const cards = slots.map(s => s.value!.cardId);

    try {
      const result = await submitAnswer(playerId, cards);
      if (result.success) {
        const isCorrect = result.is_correct || false;
        setLastResult({ correct: isCorrect, message: isCorrect ? 'Correct!' : 'Try again' });

        if (!isCorrect) {
          setTimeout(() => {
            handleClear();
          }, 1500);
        }
      } else {
        setLastResult({ correct: false, message: result.error || 'Submission failed' });
      }
      setTimeout(() => setLastResult(null), 2000);
    } catch (e) {
      setLastResult({ correct: false, message: 'Connection error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExitGame = async () => {
    try {
      await leaveGame(playerId);
    } catch (e) {
      console.warn('Failed to leave:', e);
    }
    navigate('/lobby', { replace: true });
  };

  const getRankDisplay = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };


  // Check if can submit
  const slots = getSlots();
  const hasSomeValue = slots.some(s => s.value !== null);
  const allFilled = slots.every(s => s.value !== null);

  // Render sentence display with slots
  const renderSentenceDisplay = () => {
    let slotIdx = 0;
    return (
      <div className="flex justify-center items-center gap-2 flex-wrap my-3 min-h-[50px]">
        {sentenceStructure.map((item, i) => {
          if (item.type === 'fixed') {
            return (
              <span key={i} className="font-serif text-xl font-bold text-white px-1">
                {item.char}
              </span>
            );
          }

          const thisSlotIdx = slotIdx;
          const hasValue = item.value !== null;
          const isCurrent = thisSlotIdx === currentSlotIndex && !isSubmitting;
          slotIdx++;

          return (
            <button
              key={i}
              className={`min-w-[50px] py-2 px-4 rounded-full font-serif text-lg font-bold transition-all
                ${hasValue
                  ? 'bg-white text-[#25384D]'
                  : 'bg-[#A2BEDB] text-[#25384D]/60'
                }
                ${isCurrent ? 'ring-2 ring-white ring-offset-2 ring-offset-[#83ADDF]' : ''}
              `}
              onClick={() => !isSubmitting && setCurrentSlotIndex(thisSlotIdx)}
            >
              {hasValue ? item.value!.char : item.displayLabel}
            </button>
          );
        })}
      </div>
    );
  };

  // Render bottom map with 9 task cards in grid layout
  const renderBottomMap = () => {
    const players = gameState?.players || [];
    const totalLevels = 9;

    // Group players by their current level
    const playersByLevel: Record<number, typeof players> = {};
    players.forEach(player => {
      const level = Math.min(player.current_level, totalLevels);
      if (!playersByLevel[level]) playersByLevel[level] = [];
      playersByLevel[level].push(player);
    });

    // Task card positions: row 1 has 5 cards, row 2 has 4 cards
    const row1 = [1, 2, 3, 4, 5];
    const row2 = [6, 7, 8, 9];

    const renderTaskCard = (level: number) => {
      const playersAtLevel = playersByLevel[level] || [];
      return (
        <div key={level} className="relative">
          {/* Task card */}
          <div className="w-12 h-14 bg-white rounded shadow-sm flex items-center justify-center">
            <span className="font-serif text-[8px] font-bold text-[#3C4959]/60">
              Task {level}
            </span>
          </div>
          {/* Player avatars on this card */}
          {playersAtLevel.length > 0 && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex -space-x-2">
              {playersAtLevel.map((player, idx) => (
                <div
                  key={player.player_id}
                  className={`transition-all duration-500 ease-out ${player.player_id === playerId ? 'ring-2 ring-white rounded-full z-10' : ''}`}
                  style={{ zIndex: playersAtLevel.length - idx }}
                >
                  <Avatar seatIndex={player.seat_index} size={24} />
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    return (
      <div
        className="bg-white rounded-[36px] p-2 cursor-pointer overflow-hidden shadow-lg"
        onClick={() => setShowMap(true)}
      >
        {/* Inner blue container */}
        <div className="bg-[#A2BEDB] rounded-[28px] p-3 flex justify-center">
          {/* Task cards grid - fixed width, centered */}
          <div className="w-[280px]">
            {/* Row 1: Task Cards 1-5 */}
            <div className="flex justify-center gap-2 mb-2">
              {row1.map(level => renderTaskCard(level))}
            </div>
            {/* Row 2: Task Cards 6-9 */}
            <div className="flex justify-center gap-2">
              {row2.map(level => renderTaskCard(level))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render full map modal
  const renderGameMap = () => {
    const players = gameState?.players || [];
    const totalLevels = 9;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 z-[1000]" onClick={() => setShowMap(false)}>
        <div className="bg-white border border-black/25 rounded-[48px] p-6 w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-serif text-xl font-bold text-black m-0">Game Progress</h3>
            <button className="w-8 h-8 bg-[#8EA6C0] rounded-full text-white border-none cursor-pointer font-serif font-bold" onClick={() => setShowMap(false)}>✕</button>
          </div>
          <div className="flex flex-col gap-2">
            {Array.from({ length: totalLevels }, (_, i) => i + 1).map((level) => {
              const playersAtLevel = players.filter(p => p.current_level === level);
              return (
                <div key={level} className="flex items-center gap-3 bg-[#A2BEDB]/30 rounded-xl p-3">
                  <div className="w-10 h-10 bg-[#8EA6C0] rounded-full flex items-center justify-center text-white font-serif font-bold">{level}</div>
                  <div className="flex flex-wrap gap-1 items-center">
                    {playersAtLevel.map((player) => (
                      <div
                        key={player.player_id}
                        className={`${player.player_id === playerId ? 'ring-2 ring-[#25384D] rounded-full' : ''}`}
                      >
                        <Avatar seatIndex={player.seat_index} size={24} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="flex items-center gap-3 bg-[#32BD5A]/20 rounded-xl p-3 border border-[#32BD5A]/30">
              <div className="w-10 h-10 bg-[#32BD5A] rounded-full flex items-center justify-center text-xl">🏁</div>
              <div className="flex flex-wrap gap-1 items-center">
                {players.filter(p => p.progress >= totalLevels).map((player) => (
                  <div
                    key={player.player_id}
                    className={`${player.player_id === playerId ? 'ring-2 ring-[#25384D] rounded-full' : ''}`}
                  >
                    <Avatar seatIndex={player.seat_index} size={24} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-6">
            <button
              className="bg-[#8EA6C0] text-white py-3 px-10 border-none rounded-full font-serif text-lg font-bold cursor-pointer"
              onClick={() => setShowMap(false)}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render progressive results
  const renderProgressiveResults = () => {
    if (!showProgressiveResults) return null;
    const players = gameState?.players || [];
    const myRank = myPlayer?.finish_rank;

    // Sort all players by progress (descending), then by finish_rank if available
    const sortedPlayers = [...players].sort((a, b) => {
      // Finished players (with rank) come first, sorted by rank
      if (a.finish_rank && b.finish_rank) return a.finish_rank - b.finish_rank;
      if (a.finish_rank) return -1;
      if (b.finish_rank) return 1;
      // Then sort by progress
      return (b.progress || 0) - (a.progress || 0);
    });

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center p-5 z-[1000]">
        {/* White outer card */}
        <div className="bg-white rounded-[48px] p-4 w-full max-w-[320px]">
          {/* Blue inner card */}
          <div className="bg-[#0788D9] rounded-[32px] py-6 px-5 w-full text-center">
            {/* Title */}
            <h1 className="font-serif text-[28px] font-bold text-black mb-2">Game Over</h1>
            {/* Trophy */}
            <div className="text-3xl mb-3">🏆</div>
            {/* Your Rank */}
            <p className="font-serif text-lg font-bold text-black mb-5">Your Rank：#{myRank || '-'}</p>

            {/* All players */}
            <div className="flex flex-col gap-2">
              {sortedPlayers.map((player) => {
                const isMe = player.player_id === playerId;
                return (
                  <div key={player.player_id} className="flex items-center px-2">
                    <Avatar seatIndex={player.seat_index} size={40} />
                    <span className="font-serif text-lg font-bold text-black ml-2 flex-1 text-left">
                      {player.player_id}{isMe && ' (You)'}
                    </span>
                    <span className="font-serif text-lg font-bold text-black">{player.progress || 0}/9</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Leave Now button */}
          <button
            className="bg-[#0788D9] text-black py-3 px-12 border-none rounded-full font-serif text-lg font-bold cursor-pointer mt-4 w-full"
            onClick={() => navigate('/lobby', { replace: true })}
          >
            Leave Now
          </button>
        </div>
      </div>
    );
  };

  if (isValidating) {
    return (
      <div className="min-h-dvh w-screen bg-[#3C4959] flex justify-center items-center overflow-hidden">
        <div className="text-white font-serif text-xl font-bold">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh w-screen relative overflow-hidden" style={{ background: 'transparent' }}>
      {/* AR Camera iframe - fills the screen behind UI */}
      <iframe
        ref={iframeRef}
        src="/ar-camera.html"
        title="AR Camera"
        className="fixed top-0 left-0 w-screen border-none z-0"
        style={{ height: '100dvh' }}
        allow="camera *; microphone *; accelerometer; gyroscope"
        allowFullScreen
      />

      {/* Top Background - extends down to cover question card area */}
      <div className="fixed top-0 left-0 right-0 h-[120px] bg-[#3C4959] z-40"
           style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }} />

      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 px-4 py-3 pt-[calc(12px+env(safe-area-inset-top,0px))] flex items-center gap-3 z-50">
        <div className="flex-1 font-serif text-white font-bold text-base truncate">
          Task{currentLevel?.level_id || '?'}
        </div>
        <div className="font-serif text-white font-bold text-lg min-w-[50px] text-center">
          {gameState?.game_time_remaining || 0}s
        </div>
        <button
          className="bg-[#8EA6C0] text-white py-2 px-4 rounded-full font-serif text-sm font-bold border-none cursor-pointer"
          onClick={() => setShowExitConfirm(true)}
        >
          Leave
        </button>
      </div>

      {/* Question Card */}
      <div className="fixed top-[calc(56px+env(safe-area-inset-top,0px))] left-4 right-4 bg-[#83ADDF] rounded-[24px] p-4 z-50">
        <p className="font-serif text-sm text-white/90 text-center mb-1 font-bold">
          {currentLevel?.task_description || 'Tap a card to fill the slot'}
        </p>
        {currentLevel?.task_image && (
          <img
            src={currentLevel.task_image}
            alt=""
            className="max-w-full max-h-[100px] mx-auto my-2 rounded-lg"
          />
        )}
        {renderSentenceDisplay()}
      </div>

      {/* Bottom Background - extends up to cover map and buttons area */}
      <div className="fixed bottom-0 left-0 right-0 h-[220px] bg-[#3C4959] z-40"
           style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} />

      {/* Bottom Section - Map + Buttons */}
      <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))] z-50 flex flex-col gap-4">
        {/* Player Map */}
        {!showProgressiveResults && renderBottomMap()}

        {/* Action Buttons - matching Figma style */}
        <div className="flex gap-4 justify-center">
          <button
            className={`min-w-[120px] bg-[#8EA6C0] text-white py-4 px-8 rounded-full font-serif text-lg font-bold border-none shadow-lg transition-all
              ${hasSomeValue && !isSubmitting ? 'cursor-pointer active:scale-[0.98]' : 'opacity-60 cursor-not-allowed'}`}
            onClick={handleClear}
            disabled={!hasSomeValue || isSubmitting}
          >
            Clear
          </button>
          <button
            className={`min-w-[160px] bg-[#8EA6C0] text-white py-4 px-10 rounded-full font-serif text-lg font-bold border-none shadow-lg transition-all
              ${allFilled && !isSubmitting ? 'cursor-pointer active:scale-[0.98]' : 'opacity-60 cursor-not-allowed'}`}
            onClick={handleSubmit}
            disabled={!allFilled || isSubmitting}
          >
            {isSubmitting ? '...' : 'Submit'}
          </button>
        </div>
      </div>

      {/* Disconnected indicator */}
      {!isConnected && (
        <div className="fixed top-[calc(120px+env(safe-area-inset-top,0px))] left-1/2 -translate-x-1/2 bg-[#EF4444] text-white py-2 px-5 rounded-full font-serif text-sm font-bold z-[60] animate-pulse shadow-lg">
          Disconnected
        </div>
      )}

      {/* Result Popup */}
      {lastResult && (
        <div className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 py-6 px-14 rounded-[32px] font-serif text-2xl font-bold text-center z-[200] animate-fade-in
          ${lastResult.correct ? 'bg-[#32BD5A] text-white shadow-[0_0_40px_rgba(50,189,90,0.5)]' : 'bg-[#EF4444] text-white shadow-[0_0_40px_rgba(239,68,68,0.5)]'}`}>
          {lastResult.correct ? '✓ ' : '✗ '}{lastResult.message}
        </div>
      )}

      {/* Modals */}
      {renderProgressiveResults()}
      {showMap && !showProgressiveResults && renderGameMap()}

      {/* Exit Confirm Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-5 z-[9999]">
          <div className="bg-white border border-black/25 rounded-[48px] py-8 px-6 w-full max-w-[320px] text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="font-serif text-xl font-bold text-black mb-2">Leave Without Completing?</h2>
            <p className="font-serif text-sm font-bold text-black/60 mb-6">No record will be saved, and this will be marked as incomplete.</p>
            <div className="flex gap-3 justify-center">
              <button
                className="bg-[#A2BEDB] text-[#25384D] py-3 px-8 rounded-full font-serif text-base font-bold cursor-pointer border-none transition-all active:scale-[0.96]"
                onClick={() => setShowExitConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="bg-[#EF4444] text-white py-3 px-8 rounded-full font-serif text-base font-bold cursor-pointer border-none transition-all active:scale-[0.96]"
                onClick={handleExitGame}
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WebSocket Error */}
      {wsError && (
        <div className="fixed bottom-[180px] left-5 right-5 bg-[#EF4444] text-white py-3 px-4 rounded-full font-serif text-sm font-bold text-center z-[100] shadow-lg">
          {wsError}
        </div>
      )}
    </div>
  );
};

export default GamePage;
